import '@nx/ui/eo';
import { EOToastHost, EOIcon } from '@nx/ui/eo';
import { useI18n } from '@nx/i18n';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useEffect, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { logoutRequested } from '../../store/slices/authSlice';
import { eventsWatchRequested, eventLoadRequested } from '../../store/slices/eventsSlice';
import { useEoToast } from '../../hooks/useEoToast';

function initials(name: string | null | undefined, email: string | null | undefined): string {
  const base = name || email || '?';
  return base
    .split(/\s+/)
    .map((s) => s[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

export function EOLayout() {
  const { t } = useI18n();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { eventId } = useParams();
  const { identity } = useAppSelector((s) => s.auth);
  const events = useAppSelector((s) => s.events.items);
  const participants = useAppSelector((s) => s.participants.items);
  const partners = useAppSelector((s) => s.partners.items);
  const checklist = useAppSelector((s) => s.checklist.items);
  const { toasts } = useEoToast();

  const currentEvent = useMemo(
    () => events.find((e) => e.id === eventId) ?? null,
    [events, eventId],
  );

  useEffect(() => {
    if (identity?.userId) {
      dispatch(eventsWatchRequested(identity.userId));
    }
  }, [dispatch, identity?.userId]);

  useEffect(() => {
    if (eventId) {
      dispatch(eventLoadRequested(eventId));
    }
  }, [dispatch, eventId]);

  const activeNav = useMemo(() => {
    const p = location.pathname;
    if (p.includes('/participants')) return 'participants';
    if (p.includes('/poster')) return 'poster';
    if (p.includes('/partners')) return 'partners';
    if (p.includes('/checklist')) return 'checklist';
    if (p.includes('/agenda')) return 'agenda';
    if (p.startsWith('/eo/events')) return 'events';
    return 'events';
  }, [location.pathname]);

  const openChecklistCount = checklist.filter((t) => t.status !== 'done').length;

  const barTitle = useMemo(() => {
    const p = location.pathname;
    if (p.endsWith('/new')) return t('eo.nav.newEvent');
    if (p.includes('/agenda')) return t('eo.nav.agenda');
    if (p.includes('/participants/import')) return t('eo.nav.import');
    if (p.includes('/participants')) return t('eo.nav.participants');
    if (p.includes('/poster')) return t('eo.nav.poster');
    if (p.includes('/partners/') && eventId) return t('eo.nav.partnerDetail');
    if (p.includes('/partners')) return t('eo.nav.partners');
    if (p.includes('/checklist')) return t('eo.nav.checklist');
    return t('eo.nav.events');
  }, [location.pathname, t, eventId]);

  const barSub = currentEvent?.title ?? t('eo.workspace.subtitle');

  return (
    <div className="eo-shell">
      <aside className="eo-side">
        <div className="eo-side-brand">
          <strong>N‑Excelsior</strong>
          <small>{t('eo.workspace.title')}</small>
        </div>

        <p className="eo-side-section">{t('eo.nav.overview')}</p>
        <button
          type="button"
          className={`eo-nav ${activeNav === 'events' ? 'on' : ''}`}
          onClick={() => navigate('/eo/events')}
        >
          <EOIcon n="cal" />
          <span>{t('eo.nav.events')}</span>
          <span className="badge">{events.length}</span>
        </button>

        {currentEvent ? (
          <div className="eo-event-pill">
            <p className="ep-eyebrow">{t('eo.nav.currentEvent')}</p>
            <strong>{currentEvent.title}</strong>
            <small>
              {currentEvent.startDate.toISOString().slice(0, 10)} —{' '}
              {currentEvent.endDate.toISOString().slice(0, 10)}
            </small>
            <button type="button" className="swap" onClick={() => navigate('/eo/events')}>
              {t('eo.nav.switch')} →
            </button>
          </div>
        ) : null}

        {eventId ? (
          <>
            <p className="eo-side-section">{t('eo.nav.modules')}</p>
            <button
              type="button"
              className={`eo-nav ${activeNav === 'agenda' ? 'on' : ''}`}
              onClick={() => navigate(`/eo/events/${eventId}/agenda`)}
            >
              <EOIcon n="clock" />
              <span>{t('eo.nav.agenda')}</span>
            </button>
            <button
              type="button"
              className={`eo-nav ${activeNav === 'participants' ? 'on' : ''}`}
              onClick={() => navigate(`/eo/events/${eventId}/participants`)}
            >
              <EOIcon n="users" />
              <span>{t('eo.nav.participants')}</span>
              <span className="badge">{participants.length}</span>
            </button>
            <button
              type="button"
              className={`eo-nav ${activeNav === 'poster' ? 'on' : ''}`}
              onClick={() => navigate(`/eo/events/${eventId}/poster`)}
            >
              <EOIcon n="img" />
              <span>{t('eo.nav.poster')}</span>
            </button>
            <button
              type="button"
              className={`eo-nav ${activeNav === 'partners' ? 'on' : ''}`}
              onClick={() => navigate(`/eo/events/${eventId}/partners`)}
            >
              <EOIcon n="star" />
              <span>{t('eo.nav.partners')}</span>
              <span className="badge">{partners.length}</span>
            </button>
            <button
              type="button"
              className={`eo-nav ${activeNav === 'checklist' ? 'on' : ''}`}
              onClick={() => navigate(`/eo/events/${eventId}/checklist`)}
            >
              <EOIcon n="clip" />
              <span>{t('eo.nav.checklist')}</span>
              <span className="badge">{openChecklistCount}</span>
            </button>
          </>
        ) : null}

        <div className="eo-side-foot">
          <p className="eo-side-section">{t('eo.nav.account')}</p>
          <button type="button" className="eo-nav" onClick={() => navigate('/dashboard')}>
            <EOIcon n="home" />
            <span>{t('eo.nav.dashboard')}</span>
          </button>
          <button
            type="button"
            className="eo-nav"
            onClick={() => dispatch(logoutRequested())}
          >
            <EOIcon n="out" />
            <span>{t('common.signOut')}</span>
          </button>
        </div>
      </aside>

      <div>
        <header className="eo-bar">
          <div>
            <h1>{barTitle}</h1>
            <p>{barSub}</p>
          </div>
          <div className="eo-bar-right">
            <button type="button" className="eo-icon-btn" aria-label="Notifications">
              <EOIcon n="bell" size={18} />
              <span className="dot" />
            </button>
            <div className="eo-avatar">{initials(identity?.displayName, identity?.email)}</div>
          </div>
        </header>
        <main className="eo-main">
          <Outlet />
        </main>
      </div>

      <EOToastHost toasts={toasts} />
    </div>
  );
}
